// shared by the build-time generator (Bun) and the browser download flow —
// WebCrypto only, no Node built-ins
//
// The gate key derives from the phone number alone — the visitor types it
// into the phone box to unlock the phone edition, no second prompt. The 310k
// PBKDF2 rounds keep offline guessing of the ~10^9 Thai mobile space
// expensive, and each file gets a fresh random IV. Salt is fixed by design:
// the entropy is the owner's phone, not per-file randomness.

const LOCK_SALT = new TextEncoder().encode("resume-as-code/pdf-gate/v1")
const PBKDF2_ITERATIONS = 310_000

export interface PdfLockEnvelope {
    v: 1
    salt: string
    iv: string
    ct: string
}

// the generator passes the canonical digits from src/constants.ts, the
// visitor types the number — formatting (+66, spaces, dashes) must not matter
const normalizePhone = (raw: string) => raw.replace(/\D/g, "")

export const deriveGateKey = async (phone: string): Promise<CryptoKey> => {
    const material = await crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(normalizePhone(phone)),
        "PBKDF2",
        false,
        ["deriveBits"]
    )
    const bits = await crypto.subtle.deriveBits(
        { name: "PBKDF2", hash: "SHA-256", salt: LOCK_SALT, iterations: PBKDF2_ITERATIONS },
        material,
        256
    )
    return crypto.subtle.importKey("raw", bits, "AES-GCM", false, ["encrypt", "decrypt"])
}

const toBase64 = (bytes: ArrayBuffer | Uint8Array) => {
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
    let binary = ""
    for (let i = 0; i < view.length; i++) binary += String.fromCharCode(view[i])
    return btoa(binary)
}

const fromBase64 = (value: string) => Uint8Array.from(atob(value), (c) => c.charCodeAt(0))

export const sealPdf = async (pdf: BufferSource, phone: string) => {
    const key = await deriveGateKey(phone)
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, pdf))
    const envelope: PdfLockEnvelope = {
        v: 1,
        salt: toBase64(LOCK_SALT),
        iv: toBase64(iv),
        ct: toBase64(ct),
    }
    return new TextEncoder().encode(JSON.stringify(envelope))
}

export const openPdf = async (blob: BufferSource, phone: string) => {
    const envelope = JSON.parse(new TextDecoder().decode(blob)) as PdfLockEnvelope
    if (envelope.v !== 1) throw new Error("unsupported envelope version")
    const key = await deriveGateKey(phone)
    try {
        return new Uint8Array(
            await crypto.subtle.decrypt(
                { name: "AES-GCM", iv: fromBase64(envelope.iv) },
                key,
                fromBase64(envelope.ct)
            )
        )
    } catch {
        // GCM auth failure — wrong phone
        throw new Error("WRONG_GATE_INPUT")
    }
}
